import { useEffect } from "react";

import { useWebSocketJobsStore } from "../../api/websocket";

import { HIDDEN_DISCONNECT_DELAY_MS } from "./constants";

// Connects to the websocket on mount; after the tab is hidden for
// HIDDEN_DISCONNECT_DELAY_MS, disconnects. Reopens when the tab becomes visible
// again, but only if we don't already have a socket (the store's `connect` is
// not idempotent — it would leak the existing socket if called twice). If a
// close is already in flight when the tab becomes visible, waits for it to
// finish before reconnecting.
export const useVisibilityWebSocket = (url: string): void => {
	const connect = useWebSocketJobsStore((state) => state.connect);
	const disconnect = useWebSocketJobsStore((state) => state.disconnect);

	useEffect(() => {
		let disconnectTimeout: ReturnType<typeof setTimeout> | null = null;
		let pendingReconnectUnsub: (() => void) | null = null;

		const clearDisconnectTimeout = () => {
			if (disconnectTimeout !== null) {
				clearTimeout(disconnectTimeout);
				disconnectTimeout = null;
			}
		};

		const clearPendingReconnect = () => {
			if (pendingReconnectUnsub !== null) {
				pendingReconnectUnsub();
				pendingReconnectUnsub = null;
			}
		};

		const connectIfNeeded = () => {
			const state = useWebSocketJobsStore.getState();
			if (state.socket === null) {
				connect(url);
			} else if (state.isDisconnecting && pendingReconnectUnsub === null) {
				pendingReconnectUnsub = useWebSocketJobsStore.subscribe((next, prev) => {
					if (prev.socket !== null && next.socket === null) {
						clearPendingReconnect();
						if (!document.hidden) connect(url);
					}
				});
			}
		};

		if (!document.hidden) {
			connectIfNeeded();
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				clearPendingReconnect();
				clearDisconnectTimeout();
				disconnectTimeout = setTimeout(() => {
					disconnectTimeout = null;
					disconnect();
				}, HIDDEN_DISCONNECT_DELAY_MS);
			} else {
				clearDisconnectTimeout();
				connectIfNeeded();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearPendingReconnect();
			clearDisconnectTimeout();
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			disconnect();
		};
	}, [connect, disconnect, url]);
};
